import type {
  AccountId,
  ShopperAiTagId,
} from "@redotech/effect-api-shared/ids/definitions";
import { BaseRedoInput } from "@redotech/redo-web/arbiter-components/input/base-redo-text-input";
import { RedoList } from "@redotech/redo-web/arbiter-components/list/redo-list";
import CircleSpinner from "@redotech/redo-web/circle-spinner.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useRef, useState } from "react";
import { useTagSelector } from "../hooks/use-tag-selector";

export interface TagSelectorDropdownContentProps {
  currentTagIds: readonly ShopperAiTagId[];
  onTagsChanged: (newTagIds: ShopperAiTagId[]) => void;
  /**
   * When set, the picker only offers tags belonging to this CRDB account.
   * Per-ticket callers pass the ticket's accountId so connected-team
   * tickets don't show wrong-team tags; bulk callers omit it so the
   * action menu can span multiple selected tickets.
   */
  accountId?: AccountId;
}

export const TagSelectorDropdownContent = memo(
  function TagSelectorDropdownContent({
    currentTagIds,
    onTagsChanged,
    accountId,
  }: TagSelectorDropdownContentProps) {
    const {
      tagListItems,
      selectedTagIds,
      canEditTags,
      loading,
      handleTagToggled,
      searchText,
      setSearchText,
    } = useTagSelector({ currentTagIds, onTagsChanged, accountId });

    const [focusedIndex, setFocusedIndex] = useState<number | undefined>(
      undefined,
    );
    const containerRef = useRef<HTMLDivElement>(null);

    if (loading) {
      return (
        <Flex align="center" dir="column" gap="sm" p="lg">
          <Text fontSize="xs" textColor="tertiary">
            <CircleSpinner />
          </Text>
        </Flex>
      );
    }

    if (!canEditTags) {
      return (
        <Flex align="center" dir="column" gap="sm" p="lg">
          <Text fontSize="xs" textColor="tertiary">
            You don't have permission to manage tags
          </Text>
        </Flex>
      );
    }

    return (
      <Flex dir="column" gap="none" ref={containerRef}>
        <Flex
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          px="lg"
          py="md"
        >
          <BaseRedoInput
            autoFocus
            placeholder="Search tags..."
            setValue={setSearchText}
            size="sm"
            value={searchText}
          />
        </Flex>
        <Flex px="lg" py="md">
          {tagListItems.length === 0 ? (
            <Text fontSize="xs" textColor="tertiary">
              {searchText ? "No matching tags" : "No tags available"}
            </Text>
          ) : (
            <RedoList
              focusedIndex={focusedIndex}
              items={tagListItems}
              itemSelected={({ item }) => {
                // Section headers carry a placeholder `value` only to satisfy
                // the union; selecting one shouldn't toggle a tag.
                if (item.type === "section") {
                  return;
                }
                handleTagToggled(item.value);
              }}
              refToListenTo={containerRef.current}
              selectedItems={[...selectedTagIds]}
              selectionVariant="checkbox"
              setFocusedIndex={setFocusedIndex}
              size="sm"
            />
          )}
        </Flex>
      </Flex>
    );
  },
);
